/**
 * Заявки на освобождение занятого VIN: список обеих сторон, подача заявки и
 * возражение держателя.
 *
 * ПЕРЕВОРОТ блока `WEB-2.4` (спецификация §6.2, решения формы `Б12`, `Б15`).
 * Было: «форма закрыта **двумя** полями, поля загрузки файла нет ни в одном
 * виде — недостача решение автора контракта». Стало: с контракта `0.9.33`
 * тело заявки — встроенная форма `multipart` из **трёх** частей, и третья —
 * файл техпаспорта, единственным полем файла `tech_passport_file`. Экран
 * отдаёт выбранный файл слою как есть: ни типа, ни размера, ни наличия файла
 * клиент не проверяет — `422` сервера ложится под полем файла; тело собирает
 * слой, не экран. Что прежний текст утверждал, а новый не утверждает: «поля
 * файла на экране нет» — теперь оно требуется; четвёртого поля по-прежнему нет.
 *
 * Файл в списке — только у **исходящей** заявки: с файлом — ссылка ровно на
 * `url` ответа, без файла — пометка «подана без файла» (заявка до `0.9.33`).
 * У входящей о файле ничего: держателю сервер файл не отдаёт никогда, и
 * пометка солгала бы. Тип и размер файла не показываются; срок ссылки ни с
 * чем не сравнивается.
 *
 * Окно возражения назначает и считает **сервер**: `window_ends_at`
 * показывается моментом времени форматированием локали и не сравнивается
 * клиентом ни с чем — ни отсчёта, ни признака «истекло», ни вывода статуса из
 * времени. Действие возражения видно у **всякой** входящей заявки, включая ту,
 * чьё окно позади: скрыть его по времени значило бы завести правило окна в
 * клиенте, а отказ после окна отвечает сервер статусом 422.
 *
 * Реквизитов сторон на экране нет: их нет в схеме намеренно, и экран их не
 * выдумывает и не берёт из соседних ответов. Повтор живой заявки клиент не
 * подавляет — идемпотентность по паре «фирма + VIN» решает сервер ответом 200.
 * Решения по заявке здесь нет: операции не существует вовсе — предмет Admin.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { VinClaim } from '../api/contract';
import {
  activeLocale,
  vinClaimDirectionStringKey,
  vinClaimStatusStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  enumLabel,
  momentLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface VinClaimsScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

/** Введённое человеком — ровно три части тела заявки, и ни одной сверх. */
export interface VinClaimInput {
  readonly vin: string;
  readonly techPassportNumber: string;
  /** Выбранный файл техпаспорта; не выбран — части файла в теле нет. */
  readonly techPassportFile?: File | null;
}

export interface VinClaimsScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly claims: readonly VinClaim[];
  load(): Promise<void>;
  create(input: VinClaimInput): Promise<void>;
  object(claimId: string): Promise<void>;
}

/**
 * Ключ подписи входящей стороны. Сторона заявки узнаётся **ключом строителя**,
 * а не литералом значения перечисления: литералы значений в рукописном коде
 * запрещены — перечни порождаются из контракта, — а ключ собирается
 * единственным строителем и совпадает ровно тогда же, когда значение.
 */
const INCOMING_DIRECTION_KEY: keyof Strings = 'vin_claim_direction_incoming';

export function createVinClaimsScreen(options: VinClaimsScreenOptions): VinClaimsScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const form = document.createElement('form');
  const vinCaption = document.createElement('label');
  const vinCaptionText = document.createElement('span');
  const vinField = document.createElement('input');
  const vinMessage = createFieldMessageSlot();
  const techCaption = document.createElement('label');
  const techCaptionText = document.createElement('span');
  const techField = document.createElement('input');
  const techMessage = createFieldMessageSlot();
  const fileCaption = document.createElement('label');
  const fileCaptionText = document.createElement('span');
  const fileField = document.createElement('input');
  const fileMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const list = document.createElement('ul');
  const emptyNotice = document.createElement('p');
  /** Место сообщения сервера, которому не нашлось поля, — форма members.ts. */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-vin-claims';
  form.className = 'form';
  vinCaption.className = 'form-field';
  vinCaptionText.className = 'form-field__caption';
  techCaption.className = 'form-field';
  techCaptionText.className = 'form-field__caption';
  fileCaption.className = 'form-field';
  fileCaptionText.className = 'form-field__caption';
  submit.className = 'action action--primary';
  emptyNotice.className = 'list-empty';

  heading.textContent = strings.vin_claims_title;
  vinCaptionText.textContent = strings.fleet_vin;
  techCaptionText.textContent = strings.fleet_tech_passport;
  fileCaptionText.textContent = strings.vin_claim_file_label;
  submit.type = 'submit';
  submit.textContent = strings.vin_claim_create;
  emptyNotice.textContent = strings.vin_claims_empty;

  // Имена полей — имена частей формы заявки: их ровно три, и перечень закрыт.
  // У поля файла нет `accept`: тип файла судит сервер по содержимому.
  vinField.name = 'vin';
  techField.name = 'tech_passport_number';
  fileField.type = 'file';
  fileField.name = 'tech_passport_file';

  vinCaption.append(vinCaptionText, vinField, vinMessage);
  techCaption.append(techCaptionText, techField, techMessage);
  fileCaption.append(fileCaptionText, fileField, fileMessage);
  form.append(vinCaption, techCaption, fileCaption, submit);

  let state: ScreenState | null = 'loading';
  let claims: readonly VinClaim[] = [];
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;

  /** Входящая ли заявка — стороной, которую назвал сервер полем ответа. */
  function isIncoming(claim: VinClaim): boolean {
    return vinClaimDirectionStringKey(claim.direction) === INCOMING_DIRECTION_KEY;
  }

  /** Ссылка на файл техпаспорта исходящей заявки либо пометка «без файла». */
  function passportFileOf(claim: VinClaim): HTMLElement {
    const attached = claim.tech_passport_file;

    if (attached === null) {
      const missing = document.createElement('span');

      missing.className = 'vin-claim-file';
      missing.textContent = strings.vin_claim_file_missing;

      return missing;
    }

    const link = document.createElement('a');

    link.className = 'company-link';
    link.href = attached.url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = strings.vin_claim_file_link;

    return link;
  }

  function claimRow(claim: VinClaim): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');

    node.className = 'vin-claim-row';

    // Момент конца окна — подписью локали, без единого сравнения со временем.
    const windowShown = momentLabel(activeLocale(), claim.window_ends_at);

    line.textContent = [
      enumLabel(strings, vinClaimDirectionStringKey(claim.direction)),
      claim.vin,
      claim.tech_passport_number,
      enumLabel(strings, vinClaimStatusStringKey(claim.status)),
      `${strings.vin_claim_window_ends_at} ${windowShown}`,
    ].join(' ');

    node.append(line);

    // Файл техпаспорта — у исходящей и только у неё (решение формы `Б15`):
    // держателю сервер файла не отдаёт, и пометка «без файла» у входящей
    // солгала бы. Ссылка открывает ровно адрес ответа, срок её решает сервер.
    if (!isIncoming(claim)) {
      node.append(passportFileOf(claim));
    }

    // Возражение — у всякой входящей, в том числе с истёкшим окном: показать
    // ли отказ, решает сервер, а не видимость кнопки по времени.
    if (isIncoming(claim)) {
      const objection = document.createElement('button');

      objection.type = 'button';
      objection.className = 'action';
      objection.textContent = strings.vin_claim_object;
      objection.addEventListener('click', () => {
        void object(claim.id);
      });

      node.append(objection);
    }

    return node;
  }

  function render(): void {
    notice.textContent = '';

    if (state !== null && !answered) {
      // Ответа нет: показывать нечего вовсе, отказ прав и отсутствие объекта
      // дают побайтово один экран — нераскрытие.
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    const rows: HTMLElement[] = [];

    for (const claim of claims) {
      rows.push(claimRow(claim));
    }

    list.replaceChildren(...rows);
    // Состояние «пусто» принадлежит **списку**, а не экрану: пустой список —
    // пришедший ответ, и форма подачи в нём остаётся.
    element.replaceChildren(
      heading,
      notice,
      form,
      claims.length === 0 ? emptyNotice : list,
    );
  }

  /** Заявка ответа встаёт в список: по `id`, без второй строки на повтор. */
  function upsertClaim(received: VinClaim): void {
    const grown: VinClaim[] = [];
    let known = false;

    for (const claim of claims) {
      if (claim.id === received.id) {
        grown.push(received);
        known = true;
      } else {
        grown.push(claim);
      }
    }

    if (!known) {
      grown.push(received);
    }

    claims = grown;
  }

  /**
   * Исход неудачного **действия** — форма member-rights.ts: осмысленный отказ
   * сервера остаётся на экране с содержимым и показывается его словами.
   */
  function actionFailed(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const asScreen = screenStateFor({
      kind: 'failed',
      code: failure === null ? null : failure.code,
    });

    if (failure === null || failure.code === null || asScreen === 'noAccess') {
      state = asScreen;
      answered = false;
      render();

      return;
    }

    render();

    const homeless = renderFieldMessages(form, failure.details);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0 && failure.reason !== null) {
      texts.push(failure.reason);
    }

    notice.textContent = texts.join(' ');
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    claims = [];
    answered = false;
    render();

    try {
      const received = await client.listCompanyVinClaims(companyId);

      claims = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
    } catch (error) {
      claims = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function create(input: VinClaimInput): Promise<void> {
    // Введённое встаёт в форму: отказ сервера оставляет экран на форме с
    // введённым. Формата VIN клиент не проверяет — значение уходит как есть.
    vinField.value = input.vin;
    techField.value = input.techPassportNumber;
    renderFieldMessages(form, []);

    try {
      const received = await client.createVinClaim(companyId, {
        vin: input.vin,
        tech_passport_number: input.techPassportNumber,
        tech_passport_file: input.techPassportFile ?? null,
      });

      // Перерисовка по ответу: `201` даёт новую строку, `200` — ту же живую
      // заявку, и второй строки на повтор не появляется.
      upsertClaim(received);
      state = null;
      render();
    } catch (error) {
      actionFailed(error);
    }
  }

  async function object(claimId: string): Promise<void> {
    renderFieldMessages(form, []);

    // Нажатие уходит на сервер всегда: истекло ли окно, решает он.
    try {
      const received = await client.objectVinClaim(companyId, claimId);

      // Статус на экране — из ответа; повтор возражения даёт тот же результат.
      upsertClaim(received);
      state = null;
      render();
    } catch (error) {
      actionFailed(error);
    }
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    void create({
      vin: vinField.value,
      techPassportNumber: techField.value,
      techPassportFile: fileField.files?.[0] ?? null,
    });
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get claims(): readonly VinClaim[] {
      return claims;
    },

    load,
    create,
    object,
  };
}
